import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { SmtpConfig } from '../../types';
import ImportaDaSocietaModal, { SocietaSorgente, EsitoImport } from '../../components/ImportaDaSocietaModal';
import '../../styles/impostazioni.css';

type SmtpSorgente = {
  id: number; host: string; port: number; user: string;
  secure: boolean; fromEmail: string; fromName: string; attiva: boolean;
};

type Tipo = 'EMAIL' | 'PEC';

type FormData = {
  host: string; port: number; user: string; pass: string;
  secure: boolean; fromEmail: string; fromName: string;
  attiva: boolean; societaId: number | '';
};

const EMPTY: FormData = {
  host: '', port: 587, user: '', pass: '',
  secure: false, fromEmail: '', fromName: '',
  attiva: true, societaId: '',
};

type TestResult = { ok: boolean; message: string };

export default function SmtpPage() {
  const { pathname } = useLocation();
  const tipo: Tipo = pathname.toLowerCase().includes('pec') ? 'PEC' : 'EMAIL';
  const label = tipo === 'PEC' ? 'SMTP PEC' : 'SMTP Email';

  const [items, setItems]         = useState<SmtpConfig[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [modal, setModal]         = useState(false);
  const [form, setForm]           = useState<FormData>({ ...EMPTY });
  const [editId, setEditId]       = useState<number | null>(null);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const {
    societaId: fSocietaIdStr,
    setSocietaId: setFSocietaIdStr,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const fSocietaId = fSocietaIdStr === '' ? '' : Number(fSocietaIdStr);
  const setFSocietaId = (v: number | '') => setFSocietaIdStr(v === '' ? '' : String(v));

  const itemsFiltrati = useMemo(() =>
    fSocietaId === '' ? items : items.filter(s => s.societaId === fSocietaId),
    [items, fSocietaId]
  );

  const load = async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const smtpRes = await apiFetch<{ smtp: SmtpConfig[] }>(`/api/smtp?tipo=${tipo}`);
      setItems(smtpRes.smtp);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [pronta, tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    const defaultSocietaId = fSocietaId === '' ? '' : fSocietaId;
    setForm({ ...EMPTY, societaId: defaultSocietaId });
    setEditId(null); setTestResult(null); setModal(true);
  };
  const openEdit = (s: SmtpConfig) => {
    setForm({
      host: s.host, port: s.port, user: s.user, pass: s.pass,
      secure: s.secure, fromEmail: s.fromEmail, fromName: s.fromName,
      attiva: s.attiva, societaId: s.societaId ?? '',
    });
    setEditId(s.id); setTestResult(null); setModal(true);
  };
  const closeModal = () => { setModal(false); setError(''); setTestResult(null); };

  const updateForm = (patch: Partial<FormData>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setTestResult(null); // azzera il risultato del test se l'utente modifica i campi
  };

  const testSmtp = async () => {
    if (!form.host || !form.user || !form.pass) {
      setTestResult({ ok: false, message: 'Inserisci host, utente e password prima di testare.' });
      return;
    }
    setTesting(true); setTestResult(null);
    try {
      const result = await apiFetch<TestResult>('/api/smtp/test', {
        method: 'POST',
        body: JSON.stringify({ host: form.host, port: form.port, user: form.user, pass: form.pass, secure: form.secure }),
      });
      setTestResult(result);
    } catch (e: any) {
      setTestResult({ ok: false, message: e.message });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!form.host || !form.user || !form.fromEmail) {
      setError('Host, utente e indirizzo mittente sono obbligatori');
      return;
    }
    if (!form.societaId) {
      setError('Seleziona una società');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        tipo,
        port: Number(form.port),
        societaId: form.societaId || null,
      };
      if (editId) await apiFetch(`/api/smtp/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
      else await apiFetch('/api/smtp', { method: 'POST', body: JSON.stringify(body) });
      closeModal(); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare questa configurazione SMTP?')) return;
    try { await apiFetch(`/api/smtp/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  // ── Import da altra società ─────────────────────────────────────────────
  const nomeSocietaDestinazione = societa.find(s => s.id === fSocietaId)?.nome || '';

  const caricaSocietaImport = React.useCallback(async () => {
    // @api GET /api/impostazioni/importa/smtp/societa?tipo=EMAIL -> {"success":true,"data":[{"id":1,"nome":"Pianinvest","conteggio":2}]}
    const r = await apiFetch<{ data: SocietaSorgente[] }>(`/api/impostazioni/importa/smtp/societa?tipo=${tipo}`);
    return r.data;
  }, [tipo]);

  const caricaSmtpSorgente = async (sorgenteId: number) => {
    // @api GET /api/impostazioni/importa/smtp?tipo=EMAIL&sorgenteId=1 -> {"success":true,"data":[{"id":3,"host":"smtp.esempio.it","port":587,"user":"info@esempio.it","secure":false,"fromEmail":"info@esempio.it","fromName":"Esempio","attiva":true}]}
    const r = await apiFetch<{ data: SmtpSorgente[] }>(`/api/impostazioni/importa/smtp?tipo=${tipo}&sorgenteId=${sorgenteId}`);
    return r.data;
  };

  const importaSmtp = async (ids: number[]) => {
    // @api POST /api/impostazioni/importa/smtp {"tipo":"EMAIL","societaId":2,"ids":[3]} -> {"success":true,"data":{"esiti":[{"id":3,"nome":"Esempio <info@esempio.it> — smtp.esempio.it","stato":"IMPORTATO","motivo":null}]}}
    const r = await apiFetch<{ data: { esiti: EsitoImport[] } }>('/api/impostazioni/importa/smtp', {
      method: 'POST',
      body: JSON.stringify({ tipo, societaId: fSocietaId, ids }),
    });
    return r.data.esiti;
  };

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">{label}</h1>
          <p className="bop-text-muted">{tipo === 'PEC' ? 'Configurazione PEC in uscita' : 'Configurazione email in uscita'}</p>
        </div>
        <div className="bop-flex">
          <button
            className="bop-btn-secondary"
            onClick={() => setImportOpen(true)}
            disabled={fSocietaId === ''}
            title={fSocietaId === '' ? 'Seleziona prima la società di destinazione' : `Copia una configurazione ${label} da un'altra società del gruppo`}
          >
            ⤓ Importa da altra società
          </button>
          <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuova configurazione</button>
        </div>
      </div>

      {error && !modal && <div className="bop-inline-error">{error}</div>}

      {!isUnica && (
        <div className="bop-filter-bar">
          <select
            className="bop-select impostazioni-smtp-select-societa"
            value={fSocietaId}
            onChange={e => setFSocietaId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
      )}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? <div className="inline-loading">Caricamento…</div> : itemsFiltrati.length === 0 ? (
            <div className="bop-empty-state">{items.length === 0 ? `Nessuna configurazione ${label} registrata.` : `Nessuna configurazione per la società selezionata.`}</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Host</th><th>Porta</th><th>Utente</th>
                  <th>Mittente</th><th>Società</th><th>Stato</th>
                  <th className="bop-th-actions--sm"></th>
                </tr>
              </thead>
              <tbody>
                {itemsFiltrati.map((s) => (
                  <tr key={s.id}>
                    <td><strong>{s.host}</strong></td>
                    <td>{s.port}</td>
                    <td className="impostazioni-text-muted">{s.user}</td>
                    <td className="impostazioni-text-muted">{s.fromName} &lt;{s.fromEmail}&gt;</td>
                    <td className="impostazioni-text-muted">{s.societaNome || '—'}</td>
                    <td><span className={`bop-badge ${s.attiva ? 'bop-badge-success' : 'bop-badge-danger'}`}>{s.attiva ? 'Attiva' : 'Disattiva'}</span></td>
                    <td><div className="td-actions">
                      <button className="bop-btn bop-btn-ghost" onClick={() => openEdit(s)}>Modifica</button>
                      <button className="bop-btn bop-btn-ghost bop-text-danger" onClick={() => remove(s.id)}>Elimina</button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <Modal title={editId ? `Modifica ${label}` : `Nuova ${label}`} onClose={closeModal}
          footer={<><button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button><button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo…' : 'Salva'}</button></>}>
          {error && <div className="bop-inline-error">{error}</div>}
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label>Host <span className="req">*</span></label>
              <input className="bop-input" value={form.host} onChange={(e) => updateForm({ host: e.target.value })} placeholder="smtp.esempio.it" />
            </div>
            <div className="bop-form-group">
              <label>Porta</label>
              <input className="bop-input" type="number" value={form.port} onChange={(e) => updateForm({ port: Number(e.target.value) })} />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label>Utente <span className="req">*</span></label>
              <input className="bop-input" value={form.user} onChange={(e) => updateForm({ user: e.target.value })} />
            </div>
            <div className="bop-form-group">
              <label>Password</label>
              <input className="bop-input" type="password" value={form.pass} onChange={(e) => updateForm({ pass: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label>Nome mittente</label>
              <input className="bop-input" value={form.fromName} onChange={(e) => updateForm({ fromName: e.target.value })} />
            </div>
            <div className="bop-form-group">
              <label>Email mittente <span className="req">*</span></label>
              <input className="bop-input" type="email" value={form.fromEmail} onChange={(e) => updateForm({ fromEmail: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-group">
            <label>Società</label>
            <select className="bop-select" value={form.societaId} onChange={(e) => updateForm({ societaId: e.target.value === '' ? '' : Number(e.target.value) })}>
              <option value="">— Seleziona società —</option>
              {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
          <div className="bop-form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.secure} onChange={(e) => updateForm({ secure: e.target.checked })} />
              SSL/TLS (porta 465)
            </label>
          </div>
          <div className="bop-form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.attiva} onChange={(e) => updateForm({ attiva: e.target.checked })} />
              Attiva
            </label>
          </div>

          {/* ── Test connessione ──────────────────────────────────── */}
          <div className="impostazioni-smtp-test-section">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={testSmtp}
              disabled={testing || !form.host || !form.user || !form.pass}
            >
              {testing ? 'Test in corso…' : 'Testa connessione'}
            </button>
            {testResult && (
              <div className={`impostazioni-smtp-test-result ${testResult.ok ? 'impostazioni-smtp-test-success' : 'impostazioni-smtp-test-error'}`}>
                {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
              </div>
            )}
          </div>
        </Modal>
      )}

      {importOpen && (
        <ImportaDaSocietaModal<SmtpSorgente>
          titolo={`Importa ${label} da altra società`}
          destinazioneId={String(fSocietaId)}
          nomeDestinazione={nomeSocietaDestinazione}
          intro={
            <>
              Le configurazioni vengono <strong>copiate</strong> in <strong>{nomeSocietaDestinazione}</strong>,
              password compresa: saranno subito utilizzabili. Se la società ha già una
              configurazione {label}, l&apos;import viene saltato. Gli originali restano invariati.
            </>
          }
          testoRicerca={s => `${s.host} ${s.user} ${s.fromEmail} ${s.fromName}`}
          colonne={[
            { titolo: 'Host', valore: s => <strong>{s.host}:{s.port}</strong> },
            { titolo: 'Utente', valore: s => <span className="bop-text-muted">{s.user}</span> },
            { titolo: 'Mittente', valore: s => <span className="bop-text-muted">{s.fromName} &lt;{s.fromEmail}&gt;</span> },
            { titolo: 'Stato', valore: s => <span className={`bop-badge ${s.attiva ? 'bop-badge-success' : 'bop-badge-neutral'}`}>{s.attiva ? 'Attiva' : 'Disattiva'}</span> },
          ]}
          caricaSocieta={caricaSocietaImport}
          caricaElementi={caricaSmtpSorgente}
          importa={importaSmtp}
          notaEsito="Conviene provare subito la connessione dalla modifica della configurazione importata."
          onChiudi={() => setImportOpen(false)}
          onImportato={load}
        />
      )}
    </Layout>
  );
}
